const React = require('react');
const T = require('prop-types');
const { default: Styled } = require('styled-components');
const { context } = require('app-context');
const UniqueId = require('lodash/uniqueId');

const { default: IconButton } = require('@mui/material/IconButton');
const { default: InputAdornment } = require('@mui/material/InputAdornment');
const { default: VisibilityIcon } = require('@mui/icons-material/Visibility');
const { default: VisibilityOffIcon } = require('@mui/icons-material/VisibilityOff');
const { default: Button } = require('@mui/material/Button');
const CenteredLayout = require('components/CenteredLayout');
const { NavLink: Link } = require('react-router-dom');
const { default: Classes } = require('./styles.scss');
const Fonts = require('styles/fonts.json');
const Colors = require('styles/colors.json');
const { analyticsTemplates } = require('utils/analytics');
const IsEmail = require('utils/is-email');
const NearpeerLogo = require('static/nearpeer_logo_white.svg');
const PlainLayout = require('layouts/PlainLayout');
const Footer = require('components/Footer');
const { default: CircularProgress } = require('@mui/material/CircularProgress');
const FocusIndicator = require('components/FocusIndicator');
const AppDialog = require('containers/AppDialog');
const LoginTextField = require('components/LoginTextField');
const RoutingTable = require('services/auth-management/routing-table');
const AuthStateTransitions = require('services/auth-management/state-transitions');

const internals = {};

class Login extends React.PureComponent {

    static propTypes = {
        checkSchoolSSO: T.func.isRequired,
        resendEmail: T.func.isRequired,
        loginContext: T.object,
        signupContext: T.object,
        onSubmit: T.func.isRequired,
        onInvalidSubmit: T.func.isRequired,
        push: T.func.isRequired,
        updateLoginContext: T.func,
        updateSignupContext: T.func,
        isAuthenticated: T.bool,
        isVerified: T.bool,
        location: T.object,
        clearLoginMessage: T.func,
        setLoginMessage: T.func,
        rememberedEmail: T.string,
        embedded: T.bool
    }

    static defaultProps = {
        loginContext: {},
        signupContext: {},
        updateLoginContext: () => undefined,
        updateSignupContext: () => undefined,
        clearLoginMessage: () => undefined,
        setLoginMessage: () => undefined,
        rememberedEmail: '',
        embedded: false
    }

    constructor(props) {

        super(props);

        this.id = UniqueId('login-');

        this.fields = [
            'email',
            'password'
        ];

        const initialEmail = props.location?.state?.email
            || props.loginContext?.email
            || props.rememberedEmail
            || props.loginContext?.lastLoginEmail
            || '';

        this.state = {
            email: initialEmail,
            password: '',
            isSubmitting: false,
            emailErrorShow: false,
            passwordErrorShow: false,
            showCurrentPassword: false,
            isEmailStep: true,
            invalidLoginPayload: false
        };

        this.mounted = false;
        this.passwordRef = React.createRef();
    }

    componentDidMount() {

        this.mounted = true;

        this.props.clearLoginMessage();

        if (this.props.isAuthenticated && this.props.isVerified && this.mounted) {
            this.props.push(RoutingTable.getAppLoaderRoute());
        }

        if (this.props.location?.state?.email?.length && this.mounted) {
            this.emailChanged(this.props.location.state.email);
        }
    }
    componentWillUnmount() {

        this.mounted = false;
        this.props.clearLoginMessage();
    }

    emailChanged = (evt) => {

        const email = typeof evt === 'string' ? evt : evt.target.value;

        this.setState({
            email,
            isEmailStep: true
        });
    }

    processSchoolCheckResult = async ({ formattedEmail, res, signupContext }) => {

        const {
            loginContext,
            push,
            updateLoginContext,
            updateSignupContext
        } = this.props;

        const schoolCheckResult = internals.getSchoolCheckResult(res);

        const authFlow = RoutingTable.buildSchoolCheckFlow({
            email: formattedEmail,
            schoolCheckResult
        });

        updateLoginContext(authFlow.loginContext);
        updateSignupContext(authFlow.signupContext);

        const route = RoutingTable.getSchoolCheckRoute({
            authMode: authFlow.authMode,
            redirect: loginContext.redirect,
            target: loginContext.redirectTarget,
            email: formattedEmail,
            signupContext
        });

        if (route) {
            if (route.signupContext) {
                updateSignupContext(route.signupContext);
            }

            if (route.resendVerificationEmail) {
                await this.props.resendEmail({
                    email: formattedEmail,
                    schoolId: authFlow.authMode.schoolId
                });
            }

            if (route.state) {
                push(route.path, route.state);
            }
            else {
                push(route.path);
            }

            return;
        }

        setTimeout(() => {

            if (!this.mounted) {
                return;
            }

            this.setState({
                isEmailStep: false
            });

            setTimeout(() => {

                const input = this.passwordRef.current?.querySelector('input');
                input?.focus();
            }, 0);
        }, 50);

        this.setState({ isSubmitting: false });
    }

    validate = (field) => {

        const { isEmailStep } = this.state;
        const value = this.state[field];

        switch (field) {
            case 'email':

                if (!value) {
                    return 'Email is required';
                }

                if (!IsEmail(value)) {
                    return 'Invalid email address';
                }

                break;

            case 'password':

                if (!isEmailStep && !value) {
                    return 'Password is required';
                }

                break;
        }

        return null;
    }

    showError = (field, show) => {

        show = (typeof show === 'undefined') ? this.validate(field) : show;

        return () => {

            return this.setState({ [`${field}ErrorShow`]: show });
        };
    }

    error = (field) => {

        if (!this.state[`${field}ErrorShow`]) {
            return null;
        }

        const error = this.validate(field);

        if (error !== null) {
            return <div
                id={this.id + '-' + field + '-error-message'}
                role='alert'
                aria-live='assertive'
            >
                {error}
            </div>;
        }

        return null;
    }

    hasError = (field) => {

        if (this.validate(field) !== null) {
            return true;
        }

        return false;
    }

    getDescribedById = (field) => {

        if (this.hasError(field)) {
            return (this.id + '-' + field + '-error-message');
        }

        return null;
    }

    passwordChanged = (evt) => {

        this.setState({ password: evt.target.value });
    }

    _validatePassword(ev) {

        const password = ev.target.value;

        if (!password) {
            return this.setState({ passwordError: 'Password is required' });
        }

        return this.setState({ passwordError: null });
    }

    showLoginDialog = (message = internals.invalidLoginMessage, title = 'Login Error') => {

        if (!this.mounted) {
            return;
        }

        this.props.setLoginMessage({
            title,
            message
        });
    }

    submitOrError = async (ev) => {

        ev?.preventDefault();

        const { isEmailStep, email, password } = this.state;

        const {
            checkSchoolSSO,
            signupContext,
            updateLoginContext
        } = this.props;

        const formattedEmail = email.replaceAll(' ', '');

        analyticsTemplates.buttons('login', 'login: login');

        const hasErrors = this.fields.some(this.validate);

        if (hasErrors) {
            this.setState(this.fields.reduce((collector, field) => {

                collector[`${field}ErrorShow`] = true;

                return collector;
            }, {}), FocusIndicator.wrapFocusIndicatorForElement);

            return this.props.onInvalidSubmit(this.state);
        }

        FocusIndicator.onBlurHandler();

        this.setState({ isSubmitting: true });

        if (isEmailStep) {
            AuthStateTransitions.resetForEmailChange(formattedEmail);
            updateLoginContext({ email: formattedEmail });

            const res = await checkSchoolSSO({
                email: formattedEmail
            });

            if (res === undefined) {
                this.setState({ isSubmitting: false });
                this.showLoginDialog('Invalid email, please check email again.');
                return;
            }

            await this.processSchoolCheckResult({
                formattedEmail,
                res,
                signupContext
            });
        }
        else {
            let submitResult;

            try {
                updateLoginContext({ email: formattedEmail });
                submitResult = await this.props.onSubmit({
                    email: formattedEmail,
                    password
                });
            }
            catch (err) {
                // Ignore, only throws on local
            }

            if (submitResult === undefined && !internals.hasPendingMfaChallenge()) {
                // AUTH.LOGIN.FAILURE normally supplies a specific app message.
                // Keep a fallback for local/dev code paths that return undefined without dispatching.
                this.showLoginDialog();
            }

            this.setState({ isSubmitting: false });
        }
    }

    handleMouseDownCurrentPassword = (event) => {

        event.preventDefault();
    }

    handleClickShowCurrentPassword = () => {

        this.setState({
            showCurrentPassword: !this.state.showCurrentPassword
        });
    };

    renderForm() {

        const {
            isEmailStep,
            showCurrentPassword,
            email,
            password,
            passwordErrorShow
        } = this.state;

        const { StyledButton, StyledForm } = internals;

        return <StyledForm onSubmit={this.submitOrError} noValidate>
            <div className={Classes.inputs}>
                <LoginTextField
                    name='email'
                    type='email'
                    label='Email'
                    autoComplete='username'
                    autoFocus={isEmailStep}
                    onChange={this.emailChanged}
                    value={email}
                    error={this.state.emailErrorShow && this.error('email')}
                    aria-describedby={this.getDescribedById('email')}
                />
                <LoginTextField
                    ref={this.passwordRef}
                    name='password'
                    type={showCurrentPassword ? 'text' : 'password'}
                    label='Password'
                    autoComplete='current-password'
                    autoFocus={!isEmailStep}
                    onChange={this.passwordChanged}
                    value={password}
                    error={passwordErrorShow && this.error('password')}
                    aria-describedby={this.getDescribedById('password')}
                    style={{ display: isEmailStep ? 'none' : 'block' }}
                    InputProps={{
                        endAdornment:<InputAdornment position='end'>
                            <IconButton
                                aria-label={showCurrentPassword ? 'hide current password' : 'show current password'}
                                onMouseDown={this.handleMouseDownCurrentPassword}
                                onClick={this.handleClickShowCurrentPassword}
                                data-focus-outline='radius:40'
                            >
                                {showCurrentPassword && <VisibilityIcon htmlColor='#fff' />}
                                {!showCurrentPassword && <VisibilityOffIcon htmlColor='#fff' />}
                            </IconButton>
                        </InputAdornment>
                    }}
                />
            </div>
            <StyledButton
                type='submit'
                color={'primary'}
                disabled={this.state.isSubmitting}
                data-focus-outline='radius:40,padding:4'
            >
                {isEmailStep ? 'Continue' : 'Submit'}
            </StyledButton>
        </StyledForm>;
    }

    renderLinks() {

        const { embedded } = this.props;
        const { LinkContainer } = internals;

        return <div style={{ display:'flex', flexDirection:'column', alignItems:'center' }}>
            <LinkContainer>
                <Link
                    onClick={() => analyticsTemplates.buttons(
                        embedded ? 'create account' : 'login',
                        embedded ? 'landing page: create account' : 'login: create account'
                    )}
                    className={Classes.link}
                    to='/signup'
                    data-focus-outline='radius:4,padding:5'
                >
                    Create Account
                </Link>
            </LinkContainer>
            <LinkContainer>
                <Link
                    onClick={() => analyticsTemplates.buttons('login', 'login: forgot password')}
                    className={Classes.link}
                    to='/forgot-password'
                    data-focus-outline='radius:4,padding:5'
                >
                    Forgot Password
                </Link>
            </LinkContainer>
            <LinkContainer>
                <Link
                    onClick={() => analyticsTemplates.buttons('login', 'login: trouble logging in')}
                    className={Classes.link}
                    to='/login-help'
                    data-focus-outline='radius:4,padding:5'
                >
                    Trouble Logging In? Get Help
                </Link>
            </LinkContainer>
        </div>;
    }

    render() {

        const { embedded } = this.props;
        const { isSubmitting } = this.state;
        const { TitleLockup, ProgressContainer } = internals;

        if (embedded) {
            return <React.Fragment>
                {this.renderForm()}
                {this.renderLinks()}
                <AppDialog messageKey='login' />
            </React.Fragment>;
        }

        return <PlainLayout>
            <CenteredLayout
                mainClass={Classes.mainClass}
                footer={<Footer/>}
                footerClass={Classes.footerClass}
            >
                <div style={{ margin: '.67em 0 0', fontSize: '2.75em' }}>
                    <img style={{ height: '1em' }} src={NearpeerLogo} alt='' />
                </div>
                <TitleLockup>
                    <h1 className={Classes.header} tabIndex={0}>Log&nbsp;In</h1>
                    {isSubmitting && <ProgressContainer>
                        <CircularProgress size={25} thickness={6} color='text' />
                    </ProgressContainer>}
                </TitleLockup>
                {this.renderForm()}
                {this.renderLinks()}
                <div className={Classes.spacer} />
                <AppDialog messageKey='login' />
            </CenteredLayout>
        </PlainLayout>;
    }
}

module.exports = Login;

internals.getSchoolCheckResult = (res = {}) => res?.payload?.result || res?.result || res;

internals.hasPendingMfaChallenge = () => {

    const getState = context.redux?.hooks?.getState;
    const selectors = context.selectors?.all;

    if (typeof getState !== 'function') {
        return false;
    }

    const state = getState();
    const loginContext = selectors?.getLoginContext?.(state) || {};
    const signupContext = selectors?.getSignupContext?.(state) || {};

    return !!(loginContext.mfaToken || signupContext.mfaToken);
};

internals.invalidLoginMessage = 'Invalid email or password. Please ensure you\'re using the correct personal or school email.';

internals.LinkContainer = Styled.div`
    height: 27px;
`;

internals.StyledForm = Styled.form`
    width: 100%;
`;

internals.TitleLockup = Styled.div`
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    position: relative;
`;

internals.ProgressContainer = Styled.div`
    position: absolute;
    right: -40px;
    margin-bottom: 10px;
`;

internals.StyledButton = Styled(Button)`
    background-color: ${Colors.lightText};
    margin-bottom: 15px;
    height: 3em;
    width: 100%;
    &:hover {
        background-color: ${Colors.lightText}
    }
    font-family: ${Fonts.headerFont};
    font-weight: bold;
    text-transform: none;
    font-size: 1rem;
    line-height: 3em;
    border-radius: 40px;
`;
